import AppHeader from '@components/AppNavigation/AppHeader';
import { ActionsFooter, CheckboxField } from '@components/Forms';
import ApprovalDialogButton from '@components/Forms/ApprovalDialog/ApprovalDialogButton';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Divider, Grid, Typography } from '@mui/material';
import { getCustomTemplateVals } from '@utils/connectors';
import { onSubmitForm } from '@utils/forms';
import { validateRange } from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import { omit } from 'lodash';
import React, { FC, useContext, useMemo } from 'react';
import {
	ConnectorTypeEnum,
	CUSTOM_TEMPLATE_PROPS,
	IConnectorMethods,
	IConnectors,
	IExtendedForm,
	ProviderPrefix
} from 'types/connectors';
import { IPasswordSettings } from 'types/settings';
import { object } from 'yup';
import { AuthMethodRoute } from 'constants/routes';
import { MessageConnectorContext } from 'hooks/useConnectorsContext';
import AuthMethodSwitch from './AuthMethodSwitch';
import ConnectorForm from './ConnectorForm/ConnectorForm';

interface IPasswordPageProps extends IConnectorMethods<IPasswordSettings> {
	settings?: IPasswordSettings;
	activeAuthMethods: {
		enchantedlink: boolean;
		magiclink: boolean;
	};
}

const FORM_KEYS = {
	enabled: 'enabled',
	minLength: 'minLength',
	lowercase: 'lowercase',
	uppercase: 'uppercase',
	number: 'number',
	nonAlphanumeric: 'nonAlphanumeric',
	expirationWeeks: 'expirationWeeks',
	expiration: 'expiration',
	reuse: 'reuse',
	reuseAmount: 'reuseAmount',
	lock: 'lock',
	lockAttempts: 'lockAttempts',
	providerDisplayName: 'providerDisplayName',
	emailServiceProvider: 'emailServiceProvider',
	emailSubject: 'emailSubject',
	emailBody: 'emailBody',
	customEmailSubject: 'customEmailSubject',
	customEmailBody: 'customEmailBody',
	resetAuthMethod: 'resetAuthMethod'
};

const DEFAULT_FORM: IPasswordSettings = {
	...Object.keys(FORM_KEYS).reduce(
		(prev, current) => ({ ...prev, [current]: '' }),
		{}
	),
	[FORM_KEYS.enabled]: false,
	[FORM_KEYS.minLength]: 8,
	[FORM_KEYS.lowercase]: true,
	[FORM_KEYS.uppercase]: true,
	[FORM_KEYS.number]: true,
	[FORM_KEYS.nonAlphanumeric]: true,
	[FORM_KEYS.expiration]: false,
	[FORM_KEYS.expirationWeeks]: 20,
	[FORM_KEYS.reuse]: false,
	[FORM_KEYS.reuseAmount]: 10,
	[FORM_KEYS.lock]: false,
	[FORM_KEYS.lockAttempts]: 5,
	[FORM_KEYS.resetAuthMethod]: null
} as IPasswordSettings;

const valRanges = {
	[FORM_KEYS.minLength]: {
		min: 5,
		max: 64
	},
	[FORM_KEYS.reuseAmount]: {
		min: 10,
		max: 50
	},
	[FORM_KEYS.lockAttempts]: {
		min: 2,
		max: 10
	},
	[FORM_KEYS.expirationWeeks]: {
		min: 1,
		max: 999
	}
};

const numberInputWidth = 144;
const cellSpacing = 1;

const PasswordPage: FC<IPasswordPageProps> = ({
	settings,
	templates,
	activeAuthMethods,
	onSaveConnectors,
	onSaveSettings
}) => {
	const { addToast } = useToast();

	const { getCustomTemplates, updateCustomTemplates, setConnectors } =
		useContext(MessageConnectorContext);

	const values = useMemo<IPasswordSettings>(() => {
		const customEmailTemplates = getCustomTemplates(
			ConnectorTypeEnum.EMAIL_PROVIDERS,
			settings?.resetAuthMethod
		);
		return {
			...DEFAULT_FORM,
			...settings,
			...{
				customEmailBody: customEmailTemplates.emailBody,
				customEmailSubject: customEmailTemplates.emailSubject
			}
		};
	}, [settings, getCustomTemplates]);

	const formik = useFormik<IExtendedForm<IPasswordSettings>>({
		initialValues: values,
		enableReinitialize: true,
		validationSchema: object({
			[FORM_KEYS.minLength]: validateRange(valRanges[FORM_KEYS.minLength]),
			[FORM_KEYS.reuseAmount]: validateRange(valRanges[FORM_KEYS.reuseAmount]),
			[FORM_KEYS.lockAttempts]: validateRange(
				valRanges[FORM_KEYS.lockAttempts]
			),
			[FORM_KEYS.expirationWeeks]: validateRange(
				valRanges[FORM_KEYS.expirationWeeks]
			)
		}),
		onSubmit: (vals) => {
			const customEmailTemplates = getCustomTemplates(
				ConnectorTypeEnum.EMAIL_PROVIDERS,
				formik.values.resetAuthMethod
			);

			onSubmitForm(onSaveConnectors)(
				updateCustomTemplates(
					{
						customEmailBody:
							formik.values.customEmailBody || customEmailTemplates.emailBody,
						customEmailSubject:
							formik.values.customEmailSubject ||
							customEmailTemplates.emailSubject
					},
					formik.values.resetAuthMethod
				)
			).then((connectors) => {
				setConnectors(connectors as IConnectors);
				onSubmitForm(
					onSaveSettings,
					addToast
				)(
					omit(
						getCustomTemplateVals(vals),
						CUSTOM_TEMPLATE_PROPS
					) as IPasswordSettings
				);
			});
		}
	});

	return (
		<Box
			display="flex"
			flexGrow={1}
			flexDirection="column"
			data-testid="legacy-password-page"
			component="form"
			overflow="hidden"
		>
			<AppHeader translation="pages.authMethods.password.title" mb={0} />

			<Box flexGrow={1} flexDirection="column" overflow="auto" p={4} pt={0}>
				<Grid container mt={2}>
					<Grid item xs={8} component={Alert} severity="warning">
						{intl.tr('pages.authMethods.password.subtitle')}
					</Grid>
					<AuthMethodSwitch formik={formik} />
				</Grid>
				<Divider />
				<Grid container flexGrow={1} flexDirection="row">
					<Grid item xs={12} mt={2} mb={2}>
						<Typography variant="subtitle1" sx={{ textTransform: 'uppercase' }}>
							{intl.tr(`pages.authMethods.password.policy.title`)}
						</Typography>
					</Grid>
					<Grid container display="inline-flex" mb={cellSpacing}>
						<Grid item mr={1}>
							<Typography mr={1} display="inline" lineHeight={2.4}>
								{intl.tr(
									'pages.authMethods.password.policy.require.minumumLength'
								)}
							</Typography>
							<TextFieldValidation
								id={FORM_KEYS.minLength}
								formik={formik}
								required
								type="number"
								InputProps={{
									inputProps: valRanges.minLength
								}}
								style={{ width: numberInputWidth }}
							/>
						</Grid>
					</Grid>
					<Grid item xs={12} mb={cellSpacing}>
						<CheckboxField
							id={FORM_KEYS.lowercase}
							textLabel={intl.tr(
								'pages.authMethods.password.policy.require.lowercaseLetter'
							)}
							formik={formik}
						/>
					</Grid>
					<Grid item xs={12} mb={cellSpacing}>
						<CheckboxField
							id={FORM_KEYS.uppercase}
							textLabel={intl.tr(
								'pages.authMethods.password.policy.require.uppercaseLetter'
							)}
							formik={formik}
						/>
					</Grid>
					<Grid item xs={12} mb={cellSpacing}>
						<CheckboxField
							id={FORM_KEYS.number}
							textLabel={intl.tr(
								'pages.authMethods.password.policy.require.number'
							)}
							formik={formik}
						/>
					</Grid>
					<Grid item xs={12} mb={cellSpacing}>
						<CheckboxField
							id={FORM_KEYS.nonAlphanumeric}
							textLabel={intl.tr(
								'pages.authMethods.password.policy.require.nonAlphaNumeric'
							)}
							formik={formik}
						/>
					</Grid>
					<Grid item xs={12} mb={cellSpacing}>
						<CheckboxField
							id={FORM_KEYS.expiration}
							textLabel={intl.tr(
								'pages.authMethods.password.policy.require.enableExpireWeeks'
							)}
							formik={formik}
						/>
						<TextFieldValidation
							id={FORM_KEYS.expirationWeeks}
							formik={formik}
							required
							type="number"
							InputProps={{ inputProps: valRanges.expirationWeeks }}
							style={{ width: numberInputWidth }}
						/>
						<Typography ml={2} display="inline">
							{intl.tr('pages.authMethods.password.policy.require.weeks')}
						</Typography>
					</Grid>
					<Grid item xs={12} mb={cellSpacing}>
						<CheckboxField
							id={FORM_KEYS.reuse}
							textLabel={intl.tr(
								'pages.authMethods.password.policy.require.preventReuse'
							)}
							formik={formik}
						/>
						<TextFieldValidation
							id={FORM_KEYS.reuseAmount}
							formik={formik}
							required
							type="number"
							InputProps={{ inputProps: valRanges.reuseAmount }}
							style={{ width: numberInputWidth }}
						/>
					</Grid>
					<Grid item xs={12} mb={cellSpacing}>
						<CheckboxField
							id={FORM_KEYS.lock}
							textLabel={intl.tr(
								'pages.authMethods.password.policy.require.lockAfter'
							)}
							formik={formik}
						/>
						<TextFieldValidation
							id={FORM_KEYS.lockAttempts}
							formik={formik}
							required
							type="number"
							InputProps={{ inputProps: valRanges.lockAttempts }}
							style={{ width: numberInputWidth }}
						/>
						<Typography ml={2} display="inline">
							{intl.tr('pages.authMethods.password.policy.require.attempts')}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<ConnectorForm
							providerType={ProviderPrefix.EMAIL}
							pageId={AuthMethodRoute.Password}
							formik={formik}
							templates={templates}
							enableCustomMethod
							showCustomMethodAlert={Object.values(activeAuthMethods).includes(
								false
							)}
							emailResetMethod={formik.values.resetAuthMethod}
							title="pages.authMethods.password.reset.title"
							titleTooltip="pages.authMethods.password.reset.warning"
							customMethodSelectorLabel="pages.authMethods.password.reset.method.title"
							customMethodSelectorAlert="pages.authMethods.password.reset.methods.warning"
						/>
					</Grid>
				</Grid>
			</Box>
			<ActionsFooter>
				<LoadingButton
					color="primary"
					variant="contained"
					loading={formik.isSubmitting}
					disabled={!formik.dirty}
					sx={{ marginRight: 1 }}
					onClick={() => formik.handleSubmit()}
				>
					{intl.tr('button.save')}
				</LoadingButton>
				<ApprovalDialogButton
					buttonText={intl.tr('button.cancel')}
					onApprove={formik.resetForm}
					disabled={!formik.dirty}
					title={intl.tr('discard.changes.dialog.title')}
					question={intl.tr('discard.changes.dialog.question')}
					approveText={intl.tr('discard.changes.dialog.approve')}
				/>
			</ActionsFooter>
		</Box>
	);
};

PasswordPage.defaultProps = {
	settings: undefined
};

export default PasswordPage;
